import { owlAddAjaxInfo, owlReportMetricsWeb } from '@RootCommon/utils/owl'
import { loganLog } from '@RootCommon/utils/logan'

/**
 * 延时器Worker单例类
 * 提供基于Web Worker的高精度延时器功能
 */
class TimeoutWorker {
  private static instance: TimeoutWorker | null = null
  private worker: Worker | null = null
  private callbacks: Map<string, (data: any) => void> = new Map()
  private workerCode = `
    const timeouts = {}
    let idCounter = 0

    self.onmessage = function(e) {
      const { type, id, delay } = e.data

      switch (type) {
        case 'start':
          // 创建新的延时器
          const timerId = setTimeout(() => {
            self.postMessage({ type: 'timeout', id })
            delete timeouts[id]
          }, delay)
          timeouts[id] = timerId
          break

        case 'stop':
          // 停止指定的延时器
          if (timeouts[id]) {
            clearTimeout(timeouts[id])
            delete timeouts[id]
            self.postMessage({ type: 'stopped', id })
          }
          break

        case 'stopAll':
          // 停止所有延时器
          Object.keys(timeouts).forEach(key => {
            clearTimeout(timeouts[key])
            delete timeouts[key]
          })
          self.postMessage({ type: 'allStopped' })
          break
      }
    }
  `

  /**
   * 私有构造函数，防止外部直接实例化
   */
  private constructor () {
    this.initWorker()
  }

  /**
   * 获取单例实例
   */
  public static getInstance (): TimeoutWorker {
    if (!TimeoutWorker.instance) {
      TimeoutWorker.instance = new TimeoutWorker()
    }
    return TimeoutWorker.instance
  }

  /**
   * 初始化Worker
   */
  private initWorker (): void {
    try {
      const workerBlob = new Blob([this.workerCode], { type: 'application/javascript' })
      const workerUrl = URL.createObjectURL(workerBlob)
      this.worker = new Worker(workerUrl)

      this.worker.onmessage = e => {
        const { type, id } = e.data

        if (type === 'timeout' && this.callbacks.has(id)) {
          const callback = this.callbacks.get(id)
          callback && callback(e.data)
          // 自动清理回调，因为setTimeout只执行一次
          this.callbacks.delete(id)
        }
      }

      // 清理URL对象
      URL.revokeObjectURL(workerUrl)
    } catch (error) {
      console.error('Failed to initialize TimeoutWorker:', error)
      owlAddAjaxInfo('【Timeout Worker】初始化失败', { error: JSON.stringify(error) })
      loganLog({ msg: '【Timeout Worker】初始化失败', error: JSON.stringify(error) })
      this.worker = null
    }
  }

  /**
   * 启动一个延时器
   * @param delay 延迟时间(毫秒)
   * @param callback 回调函数
   * @returns 延时器ID
   */
  public setTimeout (delay: number, callback: (data: any) => void): string {
    if (!this.worker) {
      loganLog({ msg: '【Timeout Worker】Worker未初始化' })
      return null
    }

    const id = `timeout_${Date.now()}_${Math.random().toString(36)
      .substr(2, 9)}`
    this.callbacks.set(id, callback)
    this.worker.postMessage({ type: 'start', id, delay })
    return id
  }

  /**
   * 停止指定的延时器
   * @param id 延时器ID
   */
  public clearTimeout (id: string): void {
    if (!id) return

    if (id.startsWith('fallback_') && this.callbacks.has(id)) {
      const callback = this.callbacks.get(id)
      callback && callback(null)
      this.callbacks.delete(id)
      return
    }

    if (this.worker) {
      this.worker.postMessage({ type: 'stop', id })
      this.callbacks.delete(id)
    }
  }

  /**
   * 停止所有延时器
   */
  public clearAllTimeouts (): void {
    if (this.worker) {
      this.worker.postMessage({ type: 'stopAll' })
    }

    // 清理所有回调
    this.callbacks.forEach((callback, id) => {
      if (id.startsWith('fallback_')) {
        callback(null)
      }
    })

    this.callbacks.clear()
  }

  /**
   * 销毁Worker
   */
  public destroy (): void {
    this.clearAllTimeouts()

    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }

    TimeoutWorker.instance = null
  }
}

// 导出单例实例
export default TimeoutWorker.getInstance()
